import * as React from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { InputText } from 'primereact/inputtext';

import { ApplicationState } from '../../../store';
import { services } from '../../../services';
import { alertError, alertSuccess } from '../../../store/reducers/Alert';
import { useBackgroundPromiseEffectByCondition } from '../../../helpers/hooks/useBackgroundPromiseEffectByCondition';
import { useBackgroundPromiseWrapper } from '../../../helpers/hooks/useBackgroundPromiseWrapper';

import cf from '../../../styles/card-form.module.css';
import { FormField } from '../../../components/shared/FormField/FormField';
import { Breadcrumb } from '../../../components/synced/ui/Breadcrumb/Breadcrumb';
import { PageHeading } from '../../../components/synced/ui/PageHeading/PageHeading';
import { ChildContractorUser } from '../models/ChildContractorUser';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';

export const MyUserEdit: React.FC = () => {
    const { id } = useParams();
    const idKey = Number(id ?? 0);
    const isCreate = !idKey;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userContext = useSelector((appState: ApplicationState) => appState.context.userContext);
    const [submit, isLoading] = useBackgroundPromiseWrapper();
    const [email, setEmail] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [firstName, setFirstName] = React.useState('');
    const [middleName, setMiddleName] = React.useState('');
    const [isBlocked, setIsBlocked] = React.useState(false);
    const [savedIsBlocked, setSavedIsBlocked] = React.useState(false);
    const [title, setTitle] = React.useState('');
    const [errorKeys, setErrorKeys] = React.useState<Set<string>>(new Set());
    const [reloadToken, setReloadToken] = React.useState(0);
    const isChildAccount = userContext.OwnerId != null;
    const [transferDialogVisible, setTransferDialogVisible] = React.useState(false);
    const [transferEmailConfirm, setTransferEmailConfirm] = React.useState('');
    const [transferSubmit, isTransferLoading] = useBackgroundPromiseWrapper();

    const applyUser = React.useCallback((user: ChildContractorUser) => {
        setEmail(user.Email || user.Login || '');
        setLastName(user.LastName || '');
        setFirstName(user.FirstName || '');
        setMiddleName(user.MiddleName || '');
        setTitle(user.Title || '');
        setIsBlocked(user.IsBlocked || false);
        setSavedIsBlocked(user.IsBlocked || false);
    }, []);

    const onTransferOwner = () => {
        transferSubmit(
            () => services.userService.changeOrganizationOwner(idKey),
            () => {
                setTransferDialogVisible(false);
                navigate('/');
            }
        );
    };

    const currentEmail = userContext.Login || '';
    const transferConfirmMatch = transferEmailConfirm.trim().toLowerCase() === currentEmail.toLowerCase();

    React.useEffect(() => {
        if (isChildAccount) {
            navigate('/unauthorized');
        }
    }, [isChildAccount, navigate]);

    useBackgroundPromiseEffectByCondition<ChildContractorUser>(
        () => !isCreate && !isChildAccount,
        () => services.userService.getChildUser(idKey),
        applyUser,
        [idKey, isCreate, isChildAccount, reloadToken, applyUser]
    );

    const validate = () => {
        const errors = new Set<string>();
        if (!lastName.trim()) errors.add('LastName');
        if (!firstName.trim()) errors.add('FirstName');
        if (isCreate && (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))) errors.add('Email');
        setErrorKeys(errors);
        if (errors.size > 0) {
            dispatch(alertError({ detail: 'Заполните обязательные поля' }));
        }
        return errors.size === 0;
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        const request = {
            Id: idKey,
            LastName: lastName.trim(),
            FirstName: firstName.trim(),
            MiddleName: middleName.trim(),
            Email: email.trim().toLowerCase(),
            Title: title.trim(),
            IsBlocked: isBlocked
        };

        submit(
            () =>
                isCreate
                    ? services.userService.createChildUser(request)
                    : services.userService.updateChildUser(request),
            (user) => {
                dispatch(alertSuccess({ detail: 'Пользователь сохранен' }));
                applyUser(user);
                setReloadToken((x) => x + 1);
                if (isCreate) {
                    navigate(`/my-users`);
                }
            }
        );
    };

    const emailStr = email.trim();
    const emailInvalid = emailStr.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
    const canSave =
        lastName.trim().length > 0 &&
        firstName.trim().length > 0 &&
        (!isCreate || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr));

    const inputClass = (field: string) => `${cf.input}${errorKeys.has(field) ? ` ${cf.inputError}` : ''}`;

    return (
        <div className={cf.root}>
            <Breadcrumb
                grayAncestors
                items={[
                    { label: 'Мои пользователи', to: '/my-users' },
                    { label: isCreate ? 'Создание' : 'Редактирование' }
                ]}
            />

            <PageHeading
                icon={isCreate ? 'fa-user-plus' : 'fa-user-pen'}
                title={isCreate ? 'Создать пользователя' : 'Редактировать пользователя'}
                subtitle={
                    isCreate
                        ? 'Новый пользователь получит доступ к порталу вашей организации'
                        : 'Данные пользователя вашей организации'
                }
            />

            <form onSubmit={onSubmit} noValidate className={cf.card}>
                <div className={cf.cardBody}>
                    <div className={cf.sectionLabel}>Личные данные</div>

                    <div className={cf.fieldRow}>
                        <FormField
                            label="Фамилия"
                            error={errorKeys.has('LastName') ? 'Укажите фамилию' : undefined}
                            className={cf.fieldItem}
                        >
                            <InputText
                                value={lastName}
                                className={inputClass('LastName')}
                                placeholder="Иванов"
                                onChange={(e) => setLastName(e.currentTarget.value)}
                            />
                        </FormField>
                        <FormField
                            label="Имя"
                            error={errorKeys.has('FirstName') ? 'Укажите имя' : undefined}
                            className={cf.fieldItem}
                        >
                            <InputText
                                value={firstName}
                                className={inputClass('FirstName')}
                                placeholder="Иван"
                                onChange={(e) => setFirstName(e.currentTarget.value)}
                            />
                        </FormField>
                    </div>

                    <FormField label="Отчество" hint="при наличии" className={cf.fieldItemFull}>
                        <InputText
                            value={middleName}
                            className={cf.input}
                            placeholder="Иванович"
                            onChange={(e) => setMiddleName(e.currentTarget.value)}
                        />
                    </FormField>

                    <FormField
                        label="Email"
                        error={
                            emailInvalid
                                ? 'Укажите корректный email'
                                : errorKeys.has('Email')
                                  ? 'Укажите корректный Email'
                                  : undefined
                        }
                        errorIcon={emailInvalid}
                        className={cf.fieldItemFull}
                    >
                        <InputText
                            value={email}
                            className={`${cf.input}${emailInvalid ? ` ${cf.inputError}` : ''}${!isCreate ? ` ${cf.inputDisabled}` : ''}`}
                            disabled={!isCreate}
                            placeholder="user@company.ru"
                            onChange={(e) => setEmail(e.currentTarget.value)}
                        />
                    </FormField>

                    {/* Section: Реквизиты организации */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '30px 0 16px' }}>
                        <span className={cf.sectionLabel} style={{ margin: 0 }}>
                            Реквизиты организации
                        </span>
                        <span className={cf.sectionBadge}>
                            <i className="fa-solid fa-lock" style={{ fontSize: 9 }} />
                            Заполняется автоматически
                        </span>
                    </div>

                    <div className={cf.fieldRow}>
                        <FormField label="ИНН" className={cf.fieldItem}>
                            <InputText
                                value={userContext.Inn || '—'}
                                className={`${cf.input} ${cf.inputDisabled}`}
                                disabled
                            />
                        </FormField>
                        <FormField label="КПП" className={cf.fieldItem}>
                            <InputText
                                value={userContext.Kpp || '—'}
                                className={`${cf.input} ${cf.inputDisabled}`}
                                disabled
                            />
                        </FormField>
                    </div>

                    <FormField label="SAP-коды" className={cf.fieldItemFull}>
                        <InputText
                            value={userContext.SapCodes?.length ? userContext.SapCodes.join(', ') : '—'}
                            className={`${cf.input} ${cf.inputDisabled}`}
                            disabled
                        />
                    </FormField>

                    {!isCreate && (
                        <FormField label="Блокировка пользователя" className={cf.fieldItemFull}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Checkbox
                                    inputId="isBlocked"
                                    checked={isBlocked}
                                    onChange={(e) => {
                                        setIsBlocked(e.checked ?? false);
                                    }}
                                />
                                <label htmlFor="isBlocked" style={{ cursor: 'pointer', marginBottom: 0 }}>
                                    {isBlocked ? 'Заблокирован' : 'Разблокирован'}
                                </label>
                            </div>
                        </FormField>
                    )}

                    <div className={cf.actions}>
                        <button
                            type="submit"
                            className={`${cf.btnSave}${!canSave ? ` ${cf.btnSaveDisabled}` : ''}`}
                            disabled={isLoading}
                        >
                            <i className="fa-solid fa-check" />
                            Сохранить
                        </button>
                        {!isCreate && !savedIsBlocked && (
                            <>
                                <button
                                    type="button"
                                    className={cf.btnBack}
                                    onClick={() => setTransferDialogVisible(true)}
                                >
                                    <i className="fa-solid fa-right-left" />
                                    Сменить владельца
                                </button>

                                <Dialog
                                    header="Смена владельца организации"
                                    visible={transferDialogVisible}
                                    onHide={() => {
                                        setTransferDialogVisible(false);
                                        setTransferEmailConfirm('');
                                    }}
                                    style={{ width: '480px' }}
                                    footer={
                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                            <button
                                                type="button"
                                                className={cf.btnBack}
                                                onClick={() => {
                                                    setTransferDialogVisible(false);
                                                    setTransferEmailConfirm('');
                                                }}
                                            >
                                                Отмена
                                            </button>
                                            <button
                                                type="button"
                                                className={`${cf.btnSave}${!transferConfirmMatch ? ` ${cf.btnSaveDisabled}` : ''}`}
                                                disabled={!transferConfirmMatch || isTransferLoading}
                                                onClick={onTransferOwner}
                                            >
                                                <i className="fa-solid fa-right-left" />
                                                Подтвердить перенос
                                            </button>
                                        </div>
                                    }
                                >
                                    <p style={{ marginBottom: 12 }}>
                                        Вы передаёте права владельца организации пользователю <strong>{email}</strong>.
                                        Ваш аккаунт станет дочерним.
                                    </p>
                                    <p style={{ marginBottom: 8 }}>
                                        Для подтверждения введите ваш текущий email:&nbsp;
                                        <strong style={{ userSelect: 'all' }}>{currentEmail}</strong>
                                    </p>
                                    <InputText
                                        value={transferEmailConfirm}
                                        className={cf.input}
                                        placeholder={currentEmail}
                                        onChange={(e) => setTransferEmailConfirm(e.currentTarget.value)}
                                        style={{ width: '100%' }}
                                    />
                                </Dialog>
                            </>
                        )}
                        <button type="button" className={cf.btnBack} onClick={() => navigate('/my-users')}>
                            Назад
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};
